import { useState } from 'react'
import {
  Breadcrumbs as BaseBreadcrumbs,
  BreadcrumbItem,
  Link,
} from '@overdose/components'
import { IconHome } from '@tabler/icons-react'
import classNames from 'classnames'
import { SectionContainer } from '~/components/SectionContainer'
import Typography, {
  TypographyTag,
  TypographyVariant,
} from '~/components/Typography'
import styles from './Breadcrumbs.module.css'
import { BreadcrumbsProps } from './Breadcrumbs.types'

export const Breadcrumbs = ({
  routes,
  width = 'narrow',
  compressOnMobile = true,
}: BreadcrumbsProps) => {
  // Only used when `compressOnMobile` is true: tapping the ellipsis on the
  // compressed mobile trail reveals every crumb.
  const [isMobileExpanded, setIsMobileExpanded] = useState(false)

  const lastIndex = routes.length - 1
  // Compression is only meaningful when there is something to hide between
  // the Home icon and the current page.
  const hasHiddenCrumbs = routes.length > 1
  const isMobileCompressed =
    compressOnMobile && hasHiddenCrumbs && !isMobileExpanded

  const wrapTheme = { root: styles.wrap }

  const renderHomeItem = () => {
    return (
      <BreadcrumbItem>
        <Link to='/' aria-label='Home' className={styles.link} title='Home'>
          <IconHome size={16} />
        </Link>
      </BreadcrumbItem>
    )
  }

  const renderRouteItem = (
    action: BreadcrumbsProps['routes'][number],
    i: number
  ) => {
    return (
      <BreadcrumbItem
        key={`${i}-${action.href}`}
        style={{ overflow: 'hidden' }}>
        <Typography
          tag={TypographyTag.span}
          className='overflow-hidden'
          variant={TypographyVariant.BodySmall}>
          {action.href ? (
            <Link
              to={action.href}
              aria-label={action.title}
              className={classNames(styles.title, {
                '!text-primary-heading': i === lastIndex,
              })}
              title={action.title}>
              {action.title}
            </Link>
          ) : (
            <p className='truncate'>{action.title}</p>
          )}
        </Typography>
      </BreadcrumbItem>
    )
  }

  const renderEllipsisItem = () => {
    return (
      <BreadcrumbItem>
        <button
          type='button'
          className={styles.ellipsis}
          aria-label='Show all breadcrumbs'
          aria-expanded={false}
          onClick={() => {
            setIsMobileExpanded(true)
          }}>
          <Typography
            tag={TypographyTag.span}
            variant={TypographyVariant.BodySmall}>
            …
          </Typography>
        </button>
      </BreadcrumbItem>
    )
  }

  const renderDesktopTrail = () => {
    return (
      <div
        className={classNames(
          'desktop-section hidden md:block md:pt-[18px] lg:pt-6 leading-4',
          { [styles.fullWidth]: width === 'full' }
        )}>
        <BaseBreadcrumbs maxCount={routes.length + 1}>
          {renderHomeItem()}
          {routes.map(renderRouteItem)}
        </BaseBreadcrumbs>
      </div>
    )
  }

  const renderMobileTrail = () => {
    if (isMobileCompressed) {
      // Home > … > current page. `maxCount` matches the child count so the
      // base component does not collapse the trail itself; our own tappable
      // ellipsis stands in for the hidden crumbs.
      return (
        <BaseBreadcrumbs maxCount={3}>
          {renderHomeItem()}
          {renderEllipsisItem()}
          {renderRouteItem(routes[lastIndex], lastIndex)}
        </BaseBreadcrumbs>
      )
    }

    return (
      <BaseBreadcrumbs maxCount={routes.length + 1} theme={wrapTheme}>
        {renderHomeItem()}
        {routes.map(renderRouteItem)}
      </BaseBreadcrumbs>
    )
  }

  return (
    <SectionContainer width={width} wrapperClassName='gm-breadcrumbs'>
      {renderDesktopTrail()}
      <div
        className={classNames('mobile-section', styles.mobile, {
          [styles.fullWidth]: width === 'full',
        })}>
        {renderMobileTrail()}
      </div>
    </SectionContainer>
  )
}
